import { useTranslation } from "react-i18next";
import { AppLink } from "@/components/ui";
import type {
  AuthView,
  AuthError as AuthErrorType,
  OAuthProvider,
} from "../../types/auth";
import { MagicLinkForm } from "./magic-link-form";
import { MagicLinkSent } from "./magic-link-sent";
import { AuthError } from "./auth-error";
import { AuthDivider } from "./auth-divider";
import { OAuthButtons } from "./oauth-buttons";

interface AuthFormProps {
  view: AuthView;
  email: string;
  emailError: string | null;
  isSubmitting: boolean;
  oauthConnecting: OAuthProvider | null;
  error: AuthErrorType | null;
  onEmailChange: (email: string) => void;
  onSubmitEmail: (email: string) => void;
  onOAuthClick: (provider: OAuthProvider) => void;
  onBackToForm: () => void;
  onRetry: () => void;
}

export function AuthForm({
  view,
  email,
  emailError,
  isSubmitting,
  oauthConnecting,
  error,
  onEmailChange,
  onSubmitEmail,
  onOAuthClick,
  onBackToForm,
  onRetry,
}: AuthFormProps): React.ReactElement {
  const { t } = useTranslation();

  return (
    <div className="flex w-full max-w-[420px] flex-col gap-10">
      <div className="flex flex-col gap-8">
        {view === "form" && (
          <>
            <MagicLinkForm
              email={email}
              emailError={emailError}
              isSubmitting={isSubmitting}
              isDisabled={oauthConnecting !== null}
              onEmailChange={onEmailChange}
              onSubmit={onSubmitEmail}
            />
            <AuthDivider />
            <OAuthButtons
              oauthConnecting={oauthConnecting}
              isDisabled={isSubmitting}
              onOAuthClick={onOAuthClick}
            />
          </>
        )}

        {view === "sent" && (
          <MagicLinkSent email={email} onBack={onBackToForm} />
        )}

        {view === "error" && error && (
          <AuthError error={error} onRetry={onRetry} />
        )}
      </div>

      {/* Legal links — always visible */}
      <p className="text-center text-xs text-foreground-muted leading-relaxed">
        {t("auth.legal.prefix")}{" "}
        <AppLink href="/terms" className="text-xs text-success hover:underline font-semibold">
          {t("auth.legal.terms")}
        </AppLink>{" "}
        {t("auth.legal.and")}{" "}
        <AppLink href="/privacy" className="text-xs text-success hover:underline font-semibold">
          {t("auth.legal.privacy")}
        </AppLink>
      </p>
    </div>
  );
}
